import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { STORAGE_PATHS } from '../../../constants/storagePaths';
import { uploadFile } from '../../../services/uploadFile';
import FileUpload from '../ui/FileUpload';
import Input from '../ui/Input';
import MultiSelect from '../ui/MultiSelect';
import Select from '../ui/Select';

const LeadersForm = ({ 
  initialValues, 
  onSubmit, 
  onCancel,
  campuses = [],
  ministries = [],
  cellGroups = [],
  isSubmitting
}) => {
  // Safe array access helper
  const safeArray = (arr) => Array.isArray(arr) ? arr : [];

  // Normalize initial values
  const normalizeInitialValues = (values) => {
    if (!values) return {
      name: '',
      role: [],
      phone: '',
      email: '',
      profile_image: '',
      leaderType: [],
      campusId: null,
      ministryId: null,
      cellgroupId: []
    };

    return {
      ...values,
      role: safeArray(values.role),
      leaderType: safeArray(values.leaderType),
    };
  };

  const [formData, setFormData] = useState(normalizeInitialValues(initialValues));
  const [imagePreview, setImagePreview] = useState(initialValues?.profile_image || '');

  // Define the options for each field type
  const leaderTypeOptions = [
    { value: 'campus', label: 'Campus' },
    { value: 'ministry', label: 'Ministry' },
    { value: 'cell', label: 'Cell' }
  ];

  const ministryRoleOptions = [
    { value: 'Teens Ministry Leader', label: 'Teens Ministry Leader' },
    { value: 'Youth Ministry Leader', label: 'Youth Ministry Leader' },
    { value: 'Kids Ministry Leader', label: 'Kids Ministry Leader' },
    { value: 'Women Ministry Leader', label: 'Women Ministry Leader' }
  ];

  const cellRoleOptions = [
    { value: 'Cell Group Leader', label: 'Cell Group Leader' }
  ];

  const campusRoleOptions = [
    { value: 'Regional Pastor', label: 'Regional Pastor' },
    { value: 'Assistant Leader', label: 'Assistant Leader' }
  ];

  // Get the appropriate role options based on selected leader types
  const getRoleOptions = () => {
    const allOptions = [
      ...(formData.leaderType.includes('ministry') ? ministryRoleOptions : []),
      ...(formData.leaderType.includes('cell') ? cellRoleOptions : []),
      ...(formData.leaderType.includes('campus') ? campusRoleOptions : [])
    ];
    
    // Include currently selected roles even if their type isn't selected anymore
    formData.role.forEach(role => {
      if (!allOptions.some(opt => opt.value === role)) {
        const roleLabel = [
          ...ministryRoleOptions,
          ...cellRoleOptions,
          ...campusRoleOptions
        ].find(r => r.value === role)?.label || role;
        
        allOptions.push({ value: role, label: roleLabel });
      }
    });
    
    return allOptions;
  };

  // Update form when initialValues change
  useEffect(() => {
    setFormData(normalizeInitialValues(initialValues));
    setImagePreview(initialValues?.profile_image || '');
  }, [initialValues]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArrayChange = (name, values) => {
    setFormData(prev => {
      const newData = { 
        ...prev, 
        [name]: values
      };
      
      // Reset related IDs and filter roles when leader type changes
      if (name === 'leaderType') {
        newData.campusId = null;
        newData.ministryId = null;
        newData.cellgroupId = [];
        
        // Filter roles to only keep valid ones for new types
        newData.role = prev.role.filter(role => {
          const roleTypes = {
            'Teens Ministry Leader': 'ministry',
            'Youth Ministry Leader': 'ministry',
            'Kids Ministry Leader': 'ministry',
            'Women Ministry Leader': 'ministry',
            'Cell Group Leader': 'cell',
            'Regional Pastor': 'campus',
            'Assistant Leader': 'campus'
          };
          return values.includes(roleTypes[role]);
        });
      }
      
      return newData;
    });
  };

  const handleFileChange = async (file) => {
    try {
      const profile_image = await uploadFile(file, STORAGE_PATHS.LEADERS);
      setFormData(prev => ({ ...prev, profile_image }));
      setImagePreview(URL.createObjectURL(file));
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Failed to upload profile image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare data for backend
    const submitData = {
      ...formData,
      role: safeArray(formData.role),
      leaderType: safeArray(formData.leaderType),
      cellgroupId: safeArray(formData.cellgroupId)
    };

    await onSubmit(submitData);
  };

  // Validate if roles match selected leader types
  const validateRoles = () => {
    if (formData.role.length === 0) return false;
    
    const validRoleTypes = {
      'Teens Ministry Leader': 'ministry',
      'Youth Ministry Leader': 'ministry',
      'Kids Ministry Leader': 'ministry',
      'Women Ministry Leader': 'ministry',
      'Cell Group Leader': 'cell',
      'Regional Pastor': 'campus',
      'Assistant Leader': 'campus'
    };

    return formData.role.every(role => {
      const requiredType = validRoleTypes[role];
      return !requiredType || formData.leaderType.includes(requiredType);
    });
  };

  const isFormValid = () => {
    const hasValidCellGroups = !formData.leaderType.includes('cell') || formData.cellgroupId.length > 0;
    
    return (
      formData.name &&
      formData.leaderType.length > 0 &&
      formData.role.length > 0 &&
      validateRoles() &&
      hasValidCellGroups
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Full Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
        />
        
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Leader Type(s) *
          </label>
          <MultiSelect
            options={leaderTypeOptions}
            selectedValues={formData.leaderType}
            onChange={(values) => handleArrayChange('leaderType', values)}
            placeholder="Select leader types..."
            required
          />
          {formData.leaderType.length === 0 && (
            <p className="text-xs text-red-500 mt-1">Please select at least one leader type</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Phone Number"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
        />
        
        <Input
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Role(s) *
          {formData.role.length > 1 && (
            <span className="ml-2 text-xs text-gray-500">(Multiple roles selected)</span>
          )}
        </label>
        <MultiSelect
          options={getRoleOptions()}
          selectedValues={formData.role}
          onChange={(values) => handleArrayChange('role', values)}
          placeholder="Select roles..."
          disabled={formData.leaderType.length === 0}
          required
        />
        
        {formData.leaderType.length === 0 ? (
          <p className="text-xs text-gray-500 mt-1">Please select leader type(s) first</p>
        ) : formData.role.length === 0 ? (
          <p className="text-xs text-red-500 mt-1">Please select at least one role</p>
        ) : !validateRoles() ? (
          <p className="text-xs text-red-500 mt-1">
            Some selected roles don't match current leader types
          </p>
        ) : formData.role.length > 1 && (
          <p className="text-xs text-green-600 mt-1">
            Multiple roles selected - will appear in all selected categories
          </p>
        )}
      </div>

      {formData.leaderType.includes('campus') && (
        <Select
          label="Select Campus"
          name="campusId"
          value={formData.campusId || ''}
          onChange={handleChange}
          options={[
            { value: '', label: 'Select a campus', disabled: true },
            ...campuses.map(campus => ({
              value: campus.id,
              label: campus.name
            }))
          ]}
        />
      )}

      {formData.leaderType.includes('ministry') && (
        <Select
          label="Select Ministry"
          name="ministryId"
          value={formData.ministryId || ''}
          onChange={handleChange}
          options={[
            { value: '', label: 'Select a ministry', disabled: true },
            ...ministries.map(ministry => ({
              value: ministry.id,
              label: ministry.name
            }))
          ]}
        />
      )}

      {formData.leaderType.includes('cell') && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Select Cell Group(s) *
          </label>
          <MultiSelect
            options={cellGroups.map(group => ({
              value: group.id,
              label: group.name
            }))}
            selectedValues={formData.cellgroupId}
            onChange={(values) => setFormData(prev => ({
              ...prev,
              cellgroupId: values
            }))}
            placeholder="Select cell groups..."
          />
          {formData.cellgroupId.length === 0 ? (
            <p className="text-xs text-red-500 mt-1">Please select at least one cell group</p>
          ) : formData.cellgroupId.length > 1 && (
            <p className="text-xs text-green-600 mt-1">
              Will lead {formData.cellgroupId.length} cell groups
            </p>
          )}
        </div>
      )}

      <FileUpload
        label="Profile Image"
        onFileChange={handleFileChange}
        preview={imagePreview}
        accept="image/*"
      />

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isSubmitting || !isFormValid()}
        >
          {isSubmitting ? 'Saving...' : 'Save Leader'}
        </button>
      </div>
    </form>
  );
};

export default LeadersForm;